import React, { useState } from "react";
import {
  BuildingOfficeIcon,
  MapPinIcon,
  ChevronRightIcon,
  StarIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";

const ClubSelection = ({ clubs, onClubSelect, user, loading = false }) => {
  const [selectedClub, setSelectedClub] = useState(null);

  const handleClubSelect = (club) => {
    setSelectedClub(club);
  };

  const handleContinue = () => {
    if (selectedClub) {
      onClubSelect(selectedClub.id);
    }
  };

  const getRoleDisplayName = (role) => {
    return role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "club_admin":
        return <StarSolidIcon className="w-4 h-4 text-yellow-500" />;
      case "coach":
        return <UserGroupIcon className="w-4 h-4 text-slate-500" />;
      case "evaluator":
        return <BuildingOfficeIcon className="w-4 h-4 text-green-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="mx-auto h-24 w-24 flex items-center justify-center">
          <img src="/Logo_words.png" alt="Logo" className="h-24 w-24" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Select Club
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Welcome back, {user.name}! You have access to multiple clubs.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-3">
            {clubs.map((club) => (
              <button
                key={club.id}
                onClick={() => handleClubSelect(club)}
                disabled={loading}
                className={`w-full flex items-center justify-between p-4 border-2 rounded-lg transition-all duration-200 ${
                  selectedClub?.id === club.id
                    ? "border-sky-500 bg-sky-50 shadow-md"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                } ${
                  loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <BuildingOfficeIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="flex flex-col items-start text-left">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">
                        {club.name}
                      </span>
                      {club.is_primary && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                          <StarIcon className="w-3 h-3 mr-1" />
                          Primary
                        </span>
                      )}
                    </div>
                    {club.location && (
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <MapPinIcon className="w-3 h-3 mr-1" />
                        {club.location}
                      </div>
                    )}
                    <div className="flex items-center mt-1 text-sm text-gray-600">
                      {getRoleIcon(club.role)}
                      <span className="ml-1">
                        {getRoleDisplayName(club.role)}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={`transition-colors ${
                    selectedClub?.id === club.id
                      ? "text-sky-600"
                      : "text-gray-400"
                  }`}
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6">
            <button
              onClick={handleContinue}
              disabled={!selectedClub || loading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
                selectedClub && !loading
                  ? "bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Logging in...
                </div>
              ) : (
                `Continue to ${selectedClub?.name || "Selected Club"}`
              )}
            </button>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              You can switch between clubs anytime from the dashboard
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Club switcher component for navbar
const ClubSwitcher = ({
  currentClub,
  availableClubs,
  onSwitchClub,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSwitchClub = (clubId) => {
    setIsOpen(false);
    onSwitchClub(clubId);
  };

  const getRoleDisplayName = (role) => {
    return role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (!availableClubs || availableClubs.length <= 1) {
    return (
      <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700">
        <BuildingOfficeIcon className="w-4 h-4" />
        <span>{currentClub?.name || "Club"}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50"
      >
        <BuildingOfficeIcon className="w-4 h-4" />
        <span className="max-w-32 truncate">{currentClub?.name || "Club"}</span>
        <ChevronRightIcon
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute z-20 mt-1 w-80 bg-white shadow-lg border border-gray-200 rounded-md py-1 max-h-96 overflow-y-auto">
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
              Your Clubs
            </div>
            {availableClubs.map((club) => (
              <button
                key={club.club_id}
                onClick={() => handleSwitchClub(club.club_id)}
                disabled={loading}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 disabled:opacity-50 transition-colors ${
                  club.club_id === currentClub?.id
                    ? "bg-sky-50 text-sky-700 border-r-2 border-sky-500"
                    : "text-gray-700"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{club.name}</span>
                      {club.is_primary && (
                        <StarIcon className="w-3 h-3 text-yellow-500" />
                      )}
                    </div>
                    <span className="text-xs text-gray-500 mt-0.5">
                      {getRoleDisplayName(club.role)}
                    </span>
                  </div>
                  {club.club_id === currentClub?.id && (
                    <div className="text-sky-600">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export { ClubSelection, ClubSwitcher };
